import { type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { useAuth } from '../../lib/auth';
import { useT } from '../../i18n';
import type { Role } from '../../types/api';
import styles from './EntityLink.module.css';

export type EntityKind = 'client' | 'integrator';

export interface EntityLinkProps {
  kind: EntityKind;
  /** Entity id. When missing, renders plain text (no dead link). */
  id?: string | null;
  /** Display label. When empty, renders `fallback`. */
  name?: string | null;
  className?: string;
  /** Shown when name is empty, or when link cannot be rendered. */
  fallback?: ReactNode;
  /**
   * Optional extra click handler. Propagation is always stopped so a parent
   * card/row click (open drawer / start drag) does not fire.
   */
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
}

/** Path to the entity detail card (query-param deep-link opens the drawer). */
export function entityPath(kind: EntityKind, id: string): string {
  const base = kind === 'client' ? '/clients' : '/integrators';
  return `${base}?id=${encodeURIComponent(id)}`;
}

/** Integrators page is manufacturer-only; clients are available to every role. */
export function canAccessEntity(kind: EntityKind, role: Role | null | undefined): boolean {
  if (!role) return false;
  if (kind === 'client') return true;
  return role === 'MANUFACTURER';
}

/**
 * Inline drill-in link to a Client or Integrator detail card.
 * Renders plain text when the id is missing or the current role cannot visit
 * the target route (no dead links for integrator users → /integrators).
 */
export function EntityLink({
  kind,
  id,
  name,
  className,
  fallback = null,
  onClick,
}: EntityLinkProps) {
  const { user } = useAuth();
  const { t } = useT();

  const label = name?.trim() ? name : null;
  const content = label ?? fallback;
  if (content === null || content === undefined || content === '') {
    return null;
  }

  const accessible = Boolean(id) && canAccessEntity(kind, user?.role);
  if (!accessible || !id) {
    return <span className={clsx(styles.plain, className)}>{content}</span>;
  }

  // Aria needs a string; fall back to a generic entity word when only a node is provided.
  const ariaName =
    label ??
    (typeof fallback === 'string' || typeof fallback === 'number' ? String(fallback) : kind);
  const ariaLabel =
    kind === 'client'
      ? t('components.entityLink.openClient', { name: ariaName })
      : t('components.entityLink.openIntegrator', { name: ariaName });

  const stopCardHandlers = (e: MouseEvent | KeyboardEvent) => {
    // Keep the parent card/row from treating this as "open deal" / drag start.
    e.stopPropagation();
  };

  return (
    <Link
      to={entityPath(kind, id)}
      className={clsx(styles.link, className)}
      aria-label={ariaLabel}
      onClick={(e) => {
        stopCardHandlers(e);
        onClick?.(e);
      }}
      onKeyDown={stopCardHandlers}
      onPointerDown={stopCardHandlers}
    >
      {content}
    </Link>
  );
}
